import {Container, Graphics} from "pixi.js";
import {inches} from "../../const";
import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";
import {LinearDimension} from "../frameGrid/LinearDimension";

// clearance between the dimensioned box's edge and the innermost (lane 0) dimension
// line, in scene units.
export const BASE_GAP = 6 * inches;
// spacing between successive stacked dimension lanes, in scene units. Wide enough to
// clear a dimension label + its ticks.
export const LANE_STEP = 5 * inches;
// intervals whose endpoints are within this tolerance (scene units) count as
// touching, not overlapping, so adjacent segments share a lane.
const EPS = 0.5;

// one measurement to draw: a span [lo, hi] along an axis with its label value, plus
// the stacking lane it gets assigned. onSet makes the entry editable — the export
// stacks omit it and render read-only; the interactive frame grid supplies it.
export type DimEntry = {
    lo: number;
    hi: number;
    value: number;
    lane: number;
    modified?: boolean;
    onSet?: (value: number) => void;
    onClear?: () => void;
};

// greedy interval-scheduling: assign each entry the lowest lane it fits in without
// overlapping another entry already there. Shortest spans are placed first so detail
// dimensions sit on the innermost lanes and enclosing (e.g. overall) dimensions stack
// outward. Touching endpoints (within EPS) don't count as overlap, so a chain of
// adjacent segments shares one lane. Mutates each entry's `lane`.
export function assignLanes(entries: DimEntry[]) {
    const sorted = [...entries].sort((a, b) => (a.hi - a.lo) - (b.hi - b.lo) || a.lo - b.lo);
    const lanes: DimEntry[][] = [];

    for(const e of sorted) {
        let placed = false;
        for(let L = 0; L < lanes.length; L++) {
            const fits = lanes[L].every(o => e.hi <= o.lo + EPS || e.lo >= o.hi - EPS);
            if(fits) {
                e.lane = L;
                lanes[L].push(e);
                placed = true;
                break;
            }
        }
        if(!placed) {
            e.lane = lanes.length;
            lanes.push([e]);
        }
    }
}

// renders engineering-drawing dimensions stacked around a box: horizontal
// measurements across the top, vertical ones down the left, and (optionally) a
// vertical chain down the right. Each set is lane-assigned so overlapping spans stack
// clear of each other, and each dimension gets extension lines back to the box edge.
// Read-only — the dimensions carry no edit callbacks. Owns its own display container +
// dimension pool; the caller re-parents it (attach) each draw and supplies the
// collected entries.
export class DimensionStack {
    private app: DrawFrmApp;
    private container: Container;
    // extension lines from each feature edge out to its dimension line.
    private ext: Graphics;
    // a growable pool of LinearDimensions, reused across draws (hidden when unused).
    private pool: LinearDimension[] = [];

    constructor(app: DrawFrmApp) {
        this.app = app;
        this.container = new Container();
        this.ext = new Graphics();
        this.container.addChild(this.ext);
    }

    private lease(i: number): LinearDimension {
        while(this.pool.length <= i) {
            this.pool.push(new LinearDimension(this.container, this.app));
        }
        return this.pool[i];
    }

    draw(opts: {
        // the box being dimensioned, in the parent's coords.
        rect: {x: number; y: number; width: number; height: number};
        // where to re-parent this stack's container (called each draw so it survives
        // a parent rebuild).
        parent: Container;
        top?: DimEntry[];
        left?: DimEntry[];
        right?: DimEntry[];
        // extra room to leave between the box and the first lane on a side, for marks
        // drawn outside the box there.
        clearance?: {top?: number; left?: number; right?: number};
    }) {
        opts.parent.addChild(this.container);
        this.ext.clear();

        const top = opts.top ?? [];
        const left = opts.left ?? [];
        const right = opts.right ?? [];
        assignLanes(top);
        assignLanes(left);
        assignLanes(right);

        const style = this.app.dimensionStyle;
        const {x, y, width, height} = opts.rect;
        const rightEdge = x + width;
        const gapTop = BASE_GAP + (opts.clearance?.top ?? 0);
        const gapLeft = BASE_GAP + (opts.clearance?.left ?? 0);
        const gapRight = BASE_GAP + (opts.clearance?.right ?? 0);
        let idx = 0;

        // horizontals across the top: line rises BASE_GAP + lane*STEP above the box's
        // top edge, extension lines dropping to it.
        for(const e of top) {
            const ly = y - gapTop - e.lane * LANE_STEP;
            this.app.setDimensionLineStyle(this.ext, style.defaultLineColor);
            this.ext.moveTo(e.lo, y); this.ext.lineTo(e.lo, ly);
            this.ext.moveTo(e.hi, y); this.ext.lineTo(e.hi, ly);
            this.lease(idx++).draw({
                axis: "horizontal", from: e.lo, to: e.hi, cross: ly,
                value: e.value, modified: !!e.modified,
                onSet: v => e.onSet?.(v), onClear: () => e.onClear?.(),
            });
        }

        // verticals down the left: line moves BASE_GAP + lane*STEP left of the box's
        // left edge, extension lines running out to it.
        for(const e of left) {
            const lx = x - gapLeft - e.lane * LANE_STEP;
            this.app.setDimensionLineStyle(this.ext, style.defaultLineColor);
            this.ext.moveTo(x, e.lo); this.ext.lineTo(lx, e.lo);
            this.ext.moveTo(x, e.hi); this.ext.lineTo(lx, e.hi);
            this.lease(idx++).draw({
                axis: "vertical", from: e.lo, to: e.hi, cross: lx,
                value: e.value, modified: !!e.modified,
                onSet: v => e.onSet?.(v), onClear: () => e.onClear?.(),
            });
        }

        // verticals down the right: line moves BASE_GAP + lane*STEP right of the box's
        // right edge, extension lines running out to it.
        for(const e of right) {
            const rx = rightEdge + gapRight + e.lane * LANE_STEP;
            this.app.setDimensionLineStyle(this.ext, style.defaultLineColor);
            this.ext.moveTo(rightEdge, e.lo); this.ext.lineTo(rx, e.lo);
            this.ext.moveTo(rightEdge, e.hi); this.ext.lineTo(rx, e.hi);
            this.lease(idx++).draw({
                axis: "vertical", from: e.lo, to: e.hi, cross: rx,
                value: e.value, modified: !!e.modified,
                onSet: v => e.onSet?.(v), onClear: () => e.onClear?.(),
            });
        }

        // hide any pooled dimensions left over from a busier previous draw.
        for(; idx < this.pool.length; idx++) this.pool[idx].hide();
    }

    // hide everything without tearing down (e.g. when the box isn't available yet).
    hide() {
        this.ext.clear();
        this.pool.forEach(d => d.hide());
    }

    destroy() {
        this.pool.forEach(d => d.destroy());
        this.pool = [];
        if(!this.ext.destroyed) this.ext.destroy();
        // the container lives under a caller-owned parent, so a tree destroy may have
        // already freed it — guard the free.
        if(!this.container.destroyed) this.container.destroy();
    }
}

// a small helper for collecting deduped measurement entries: identical spans (within
// half a scene unit) are kept once.
export class DimCollector {
    private map = new Map<string, DimEntry>();

    add(lo: number, hi: number, value: number) {
        if(hi - lo < 1) return;
        const key = Math.round(lo * 2) + "_" + Math.round(hi * 2);
        if(!this.map.has(key)) this.map.set(key, {lo, hi, value, lane: 0});
    }

    entries(): DimEntry[] {
        return [...this.map.values()];
    }
}
